/**
 * card-variant-selects
 *
 * Inline variant selector used on product cards (collection grids, related /
 * recommendation sliders, search results). On change it resolves the variant
 * from the embedded variants JSON, fetches the product-card-swatches section
 * for that variant, and updates the card's price, image, add-to-cart button
 * and variant id in place. Modelled on product-live-swatches.js.
 */
if (!customElements.get('card-variant-selects')) {
	customElements.define(
		'card-variant-selects',
		class CardVariantSelects extends HTMLElement {
			constructor() {
				super();
				this.cardContainer = this.closest('.product-card-wrapper, .card-wrapper');
				this.addEventListener('change', this.onVariantChange.bind(this));
			}

			onVariantChange() {
				this.updateOptions();
				this.updateMasterId();
				this.updateSelectedValueLabels();
				if (this.currentVariant) this.renderCardInfo();
			}

			updateOptions() {
				this.options = Array.from(this.querySelectorAll('select, fieldset'), (element) => {
					if (element.tagName === 'SELECT') return element.value;
					if (element.tagName === 'FIELDSET') {
						return Array.from(element.querySelectorAll('input')).find((radio) => radio.checked)?.value;
					}
				});
			}

			updateMasterId() {
				this.currentVariant = this.getVariantData().find((variant) => {
					return !variant.options.map((option, index) => this.options[index] === option).includes(false);
				});
			}

			// Keep the swatch legend's displayed value in sync with the selection.
			updateSelectedValueLabels() {
				this.querySelectorAll('fieldset').forEach((fieldset) => {
					const checked = fieldset.querySelector('input:checked');
					const label = fieldset.querySelector('[data-selected-swatch-value]');
					if (checked && label) label.textContent = checked.value;
				});
			}

			renderCardInfo() {
				if (!this.cardContainer) return;
				const requestedVariantId = this.currentVariant.id;
				const sectionId = this.dataset.originalSection || this.dataset.section;

				fetch(`${this.dataset.url}?variant=${requestedVariantId}&section_id=${sectionId}`)
					.then((response) => response.text())
					.then((responseText) => {
						const html = new DOMParser().parseFromString(responseText, 'text/html');
						const container = this.cardContainer;

						const imageElement = html.getElementById('variant-image');
						const priceElement = html.getElementById('variant-price');
						const buttonElement = html.getElementById('variant-button');

						// Price (drop the min-max range so the selected variant's price shows)
						const priceBox = container.querySelector('.product-price__container');
						if (priceElement && priceBox) {
							priceElement.querySelector('.price-min-max')?.remove();
							priceBox.innerHTML = priceElement.innerHTML;
						}

						// Image (swap the card's primary image source, preserve markup)
						const newImg = imageElement?.querySelector('img');
						const cardImg = container.querySelector('.card__media .media img');
						if (newImg && cardImg) {
							cardImg.src = newImg.src;
							cardImg.srcset = newImg.srcset || newImg.src;
						}

						// Variant id (direct add-to-cart form) + quickview target
						const idInput = container.querySelector('product-form input[name="id"], .product-variant-id');
						if (idInput) idInput.value = requestedVariantId;
						const quickview = container.querySelector('product-quickview');
						if (quickview) quickview.setAttribute('data-variant-id', requestedVariantId);

						// Add-to-cart button availability + label
						const addBtn = container.querySelector('product-form .product-form__submit, .product-card__button');
						if (buttonElement && addBtn) {
							if (buttonElement.getAttribute('data-disabled') === 'true') {
								addBtn.setAttribute('disabled', 'disabled');
							} else {
								addBtn.removeAttribute('disabled');
							}
							const newText = buttonElement.querySelector('.btn-text');
							const btnText = addBtn.querySelector('.btn-text');
							if (newText && btnText) btnText.innerHTML = newText.innerHTML;
						}
					})
					.catch((e) => console.error(e));
			}

			getVariantData() {
				this.variantData =
					this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
				return this.variantData;
			}
		}
	);
}
